import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDescope, useUser, useSession } from '@descope/react-sdk';
import { User, UserRole } from '../types';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { httpClient } from './httpClient';
import { toast } from 'react-toastify';
import { setUserContext, clearUserContext, sentryLogger } from '../utils/sentry';

interface SignUpParams {
  email: string;
  password: string;
  name?: string;
  customAttributes?: Record<string, any>;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  userRole: UserRole | null;
  userId: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  signUp: (params: SignUpParams) => Promise<void>;
  updateUserRole: (role: UserRole, customAttributes?: Record<string, any>) => Promise<void>;
  refreshUser: () => Promise<User | null>;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  isOrgAdmin: () => boolean;
  isInterviewer: () => boolean;
  isCandidate: () => boolean;
  isAdmin: () => boolean;
  jwtToken: string | null; 
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isUserLoading, user: descopeUser } = useUser();
  const { sessionToken } = useSession();
  const descopeSdk = useDescope();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [jwtToken, setJwtToken] = useState<string | null>(null);

  // Extract and store JWT token when session changes
  useEffect(() => {
    if (sessionToken) {
      sentryLogger.debug("JWT token received from Descope", {
        tokenLength: sessionToken.length,
        hasAxiosAuth: !!axios.defaults.headers.common['Authorization'],
        hasHttpClientAuth: !!httpClient.defaults.headers.common['Authorization']
      });
      
      setJwtToken(sessionToken);
      
      // Set the JWT token for all axios requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
      
      // Also set default on our shared axios instance
      httpClient.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;

      // Expose globally for httpClient fallback
      (window as any).__DESCOPE_JWT__ = sessionToken;
      
      sentryLogger.info("JWT token configured for HTTP clients", {
        tokenConfigured: true,
        globalTokenSet: true
      });
      
      // Ensure both session and local storage have the token
      try {
        // Check if existing data exists to avoid corrupting other fields
        const existingSession = sessionStorage.getItem('descope.session');
        if (existingSession) {
          const sessionData = JSON.parse(existingSession);
          sessionData.token = sessionToken;
          sessionStorage.setItem('descope.session', JSON.stringify(sessionData));
        } else {
          // Create new with basic required fields
          sessionStorage.setItem('descope.session', JSON.stringify({
            token: sessionToken,
            refreshExpiry: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
          }));
        }
        
        // Do the same for localStorage
        const existingLocalData = localStorage.getItem('descope.session');
        if (existingLocalData) {
          const localData = JSON.parse(existingLocalData);
          localData.token = sessionToken;
          localStorage.setItem('descope.session', JSON.stringify(localData));
        } else {
          localStorage.setItem('descope.session', JSON.stringify({
            token: sessionToken,
            refreshExpiry: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
          }));
        }
      } catch (error) {
        console.error('Failed to store token in storage:', error);
      }
    } else {
      setJwtToken(null);
      delete axios.defaults.headers.common['Authorization'];
      delete httpClient.defaults.headers.common['Authorization'];
      delete (window as any).__DESCOPE_JWT__;
      
      // Clear storage
      try {
        const storages = [sessionStorage, localStorage];
        storages.forEach(storage => {
          const existingData = storage.getItem('descope.session');
          if (existingData) {
            const data = JSON.parse(existingData);
            if (data.token) {
              delete data.token;
              storage.setItem('descope.session', JSON.stringify(data));
            }
          }
        });
      } catch (error) {
        console.error('Failed to clear token from storage:', error);
      }
    }
  }, [sessionToken]);

  useEffect(() => {
    const initOrSyncUser = async () => {
      if (!isUserLoading && descopeUser) {
        try {
          sentryLogger.debug("Starting user synchronization", {
            descopeUserId: descopeUser.userId,
            descopeEmail: descopeUser.email,
            currentUserId: user?.id,
            currentUserRole: user?.role
          });
          
          // Get user info from backend to ensure we have latest data
          const response = await httpClient.get('/api/v1/users/me');
          const backendUser = response.data;
          
          sentryLogger.debug("Backend user data retrieved", {
            backendUserId: backendUser.id,
            backendRole: backendUser.role,
            backendOrgId: backendUser.organization_id || backendUser.organizationId
          });

          const transformedUser: User = {
            id: descopeUser.userId,
            email: descopeUser.email || '',
            name: descopeUser.name || '',
            role: backendUser.role || 'candidate',
            organizationId: backendUser.organization_id || backendUser.organizationId || '',
            picture: descopeUser.picture || '',
            createdAt: user?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          if (!user || 
              user.id !== transformedUser.id || 
              user.role !== transformedUser.role ||
              user.name !== transformedUser.name ||
              user.email !== transformedUser.email ||
              user.picture !== transformedUser.picture ||
              user.organizationId !== transformedUser.organizationId
          ) {
            console.log('[AuthContext useEffect] Syncing user from backend. Current user:', user, 'New user data:', transformedUser);
            
            const isNewUser = !user;
            const roleChanged = user && user.role !== transformedUser.role;
            const orgChanged = user && user.organizationId !== transformedUser.organizationId;
            
            sentryLogger.logUserAction("user_sync_performed", transformedUser.id, {
              isNewUser,
              roleChanged,
              orgChanged,
              oldRole: user?.role,
              newRole: transformedUser.role,
              oldOrgId: user?.organizationId,
              newOrgId: transformedUser.organizationId,
              syncSource: "backend_api"
            });
            
            setUser(transformedUser);
            setUserRole(transformedUser.role);
            setUserId(transformedUser.id);
            
            // Update Sentry user context
            setUserContext({
              id: transformedUser.id,
              email: transformedUser.email,
              role: transformedUser.role,
            });
            
            if (isNewUser) {
              sentryLogger.info("User authenticated successfully", {
                userId: transformedUser.id,
                email: transformedUser.email,
                role: transformedUser.role,
                organizationId: transformedUser.organizationId,
                authProvider: "descope"
              });
            }
          }
          
          if (window.location.pathname === '/dashboard') {
            redirectBasedOnRole(transformedUser.role);
          }
        } catch (error) {
          console.error('Error syncing user with backend:', error);
          
          sentryLogger.error("Failed to sync user with backend", {
            descopeUserId: descopeUser.userId,
            error: error instanceof Error ? error.message : String(error),
            fallbackToDescope: true
          });
          
          // On error, still set basic user info from Descope
          const transformedUser: User = {
            id: descopeUser.userId,
            email: descopeUser.email || '',
            name: descopeUser.name || '',
            role: 'candidate',
            organizationId: '',
            picture: descopeUser.picture || '',
            createdAt: user?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          
          sentryLogger.logUserAction("user_fallback_auth", transformedUser.id, {
            fallbackReason: "backend_sync_failed",
            fallbackRole: transformedUser.role,
            descopeEmail: transformedUser.email
          });
          
          setUser(transformedUser);
          setUserRole(transformedUser.role);
          setUserId(transformedUser.id);
          
          // Update Sentry user context even in error case
          setUserContext({
            id: transformedUser.id,
            email: transformedUser.email,
            role: transformedUser.role,
          });
        }
      } else if (!isUserLoading && !descopeUser) {
        if(user !== null) {
          console.log('[AuthContext useEffect] Descope user is null, logging out context user.');
          
          sentryLogger.logUserAction("user_logged_out", user.id, {
            logoutReason: "descope_session_ended",
            wasAuthenticated: true,
            previousRole: user.role
          });
          
          // Clear Sentry user context on logout
          clearUserContext();
        }
        setUser(null);
        setUserRole(null);
        setUserId(null);
      }
    };

    initOrSyncUser();
  }, [isUserLoading, descopeUser]);

  // Separate useEffect for isLoading state based on isUserLoading
  useEffect(() => {
    setIsLoading(isUserLoading);
  }, [isUserLoading]);

  const redirectBasedOnRole = (role: UserRole) => {
    switch (role) {
      case 'orgadmin':
        navigate('/org-admin');
        break;
      case 'interviewer':
        navigate('/interviewer/dashboard');
        break;
      case 'admin':
        navigate('/admin');
        break;
      case 'candidate':
      default:
        navigate('/dashboard');
        break;
    }
  };

  const handleLogin = async () => {
    try {
      sentryLogger.logUserAction("login_attempt_started", "anonymous", {
        provider: "google",
        method: "oauth"
      });
      
      await descopeSdk.oauth.start('google');
      
      sentryLogger.info("OAuth login initiated", {
        provider: "google",
        method: "oauth"
      });
      
    } catch (error) {
      console.error('Login error:', error);
      
      sentryLogger.error("Login attempt failed", {
        provider: "google",
        method: "oauth",
        error: error instanceof Error ? error.message : String(error)
      });
      
      throw error;
    }
  };

  const handleLogout = async () => {
    const currentUserId = user?.id || "unknown";
    
    try {
      sentryLogger.logUserAction("logout_attempt_started", currentUserId, {
        currentRole: user?.role,
        wasAuthenticated: !!user
      });
      
      await descopeSdk.logout();
      
      sentryLogger.logUserAction("logout_completed", currentUserId, {
        previousRole: user?.role,
        success: true
      });
      
      // Clear Sentry user context on logout
      clearUserContext();
      setUser(null);
      setUserRole(null);
      setUserId(null);
      navigate('/');
      
    } catch (error) {
      console.error('Logout error:', error);
      
      sentryLogger.error("Logout attempt failed", {
        userId: currentUserId,
        error: error instanceof Error ? error.message : String(error)
      });
      
      throw error;
    }
  };

  const handleSignUp = async (params: SignUpParams) => {
    try {
      // Sign up the user with email/password
      await descopeSdk.password.signUp(params.email, params.password, {
        name: params.name,
      });

      // After successful signup, the user will be automatically logged in
      // and their custom attributes will be available in the next session
      if (params.customAttributes) {
        // Store custom attributes in session storage for the onboarding process
        Object.entries(params.customAttributes).forEach(([key, value]) => {
          sessionStorage.setItem(`registration_${key}`, value);
        });
      }
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };
  
  // Refresh user data from Descope
  const refreshUser = async (): Promise<User | null> => {
    try {
      console.log('[AuthContext] Refreshing user data from Descope...');
      // Call the Descope me() method to get fresh user data
      const freshUserResponse = await descopeSdk.me();
      console.log('[AuthContext] Raw freshUserResponse from descope.me():', JSON.stringify(freshUserResponse, null, 2));
      
      if (freshUserResponse?.data) {
        const freshUserData = freshUserResponse.data;
        console.log('[AuthContext] Extracted freshUserData from descope.me():', JSON.stringify(freshUserData, null, 2));
        
        // --- Logic to determine role from freshUserData (descope.me()) ---
        let roleFromMeApi: UserRole | undefined = freshUserData.customAttributes?.role as UserRole | undefined;
        if (!roleFromMeApi && freshUserData.roleNames && freshUserData.roleNames.length > 0) {
          roleFromMeApi = freshUserData.roleNames[0] as UserRole;
        }
        const resolvedRoleFromMeApi = roleFromMeApi || 'candidate';
        // --- End logic ---

        const transformedUser: User = {
          id: freshUserData.userId,
          email: freshUserData.email || '',
          name: freshUserData.name || '',
          role: resolvedRoleFromMeApi, // Use the resolved role
          organizationId: freshUserData.customAttributes?.organizationId || '',
          picture: freshUserData.picture || '',
          createdAt: user?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        console.log('[AuthContext] Updated user role from refreshUser:', transformedUser.role);
        
        // Update state with fresh data
        setUser(transformedUser);
        setUserRole(transformedUser.role);
        setUserId(transformedUser.id);
        return transformedUser;
      }
      return null;
    } catch (error) {
      console.error('[AuthContext] Failed to refresh user data:', error);
      return null;
    }
  };
  
  const updateUserRole = async (role: UserRole, customAttributes?: Record<string, any>): Promise<void> => {
    if (!user || !userId) {
      console.error('[AuthContext] Cannot update role: User or UserId is null');
      toast.error('Failed to update user role. User not found.');
      return;
    }

    setIsLoading(true);
    try {
      console.log(`[AuthContext] Requesting backend to update role to: ${role} for user ID: ${userId}`);
      if (customAttributes) {
        console.log('[AuthContext] With custom attributes:', customAttributes);
      }

      // Call the backend API to update user details in Descope
      // The backend will use the Descope Management SDK and Management Key
      const response = await httpClient.post('/api/v1/users/update-descope-profile', {
        userId: userId,
        role: role,
        customAttributes: {
          role: role, // Ensure 'role' is part of customAttributes if your backend expects it
          ...customAttributes,
        },
      });

      if (response.status !== 200 && response.status !== 204) {
        // Assuming backend returns 200 or 204 on success
        let errorMessage = 'Failed to update user role via backend.';
        try {
            const errorData = response.data;
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            // Keep default error message
        }
        throw new Error(errorMessage);
      }

      console.log(`[AuthContext] Backend successfully processed role update to ${role} for user ID: ${userId}`);

      // Refresh user context to reflect changes
      // The backend should have updated Descope, so refreshUser will pull the latest
      await refreshUser(); 
      
      // Allow state to propagate
      await new Promise(resolve => setTimeout(resolve, 200));

      if (user && user.role !== role) {
         console.warn(`[AuthContext] User role in context (${user.role}) differs from target role (${role}) after update and refresh. Backend update might have an issue or a sync delay.`);
      } else if (user) {
        console.log(`[AuthContext] User role successfully synced to ${user.role} in context after backend update.`);
      }

      toast.success('User role update request processed!');
    } catch (error: any) {
      console.error('[AuthContext] Error requesting backend to update user role:', error);
      const message = error.response?.data?.message || error.message || 'Failed to update user role. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Role checking utilities
  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!userRole) return false;
    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }
    return userRole === roles;
  };
  
  const isOrgAdmin = (): boolean => userRole === 'orgadmin';
  const isInterviewer = (): boolean => userRole === 'interviewer';
  const isCandidate = (): boolean => userRole === 'candidate';
  const isAdmin = (): boolean => userRole === 'admin';

  const value = {
    isAuthenticated: !!user,
    isLoading: isLoading || isUserLoading,
    user,
    userRole,
    userId,
    jwtToken,
    login: handleLogin,
    logout: handleLogout,
    signUp: handleSignUp,
    updateUserRole,
    refreshUser,
    hasRole,
    isOrgAdmin,
    isInterviewer,
    isCandidate,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 